export type ThemeChoice = "dark" | "light";

export const THEME_STORAGE_KEY = "denker-theme";
const LEGACY_THEME_STORAGE_KEY = "theme";
export const THEME_CHANGED_EVENT = "denker:theme-changed";
export const THEME_TAURI_EVENT = "denker://theme-changed";

/** The themes Settings offers, in order. */
export const THEME_CHOICES: readonly ThemeChoice[] = ["dark", "light"];

function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === "dark" || value === "light";
}

/** The retired Clear glass theme. */
const RETIRED_CLEAR_THEME = "clear";

/**
 * Reads a theme from storage or from another window. The retired Clear glass
 * theme reads as dark: windows and tray menus from the previous release can
 * still send it. Drop that mapping in the release after this one, once no
 * running window can.
 */
export function parseThemeChoice(value: unknown): ThemeChoice | null {
  if (value === RETIRED_CLEAR_THEME) return "dark";
  return isThemeChoice(value) ? value : null;
}

export function getStoredThemeChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    const legacy = localStorage.getItem(LEGACY_THEME_STORAGE_KEY);
    const choice =
      parseThemeChoice(stored) ?? parseThemeChoice(legacy) ?? "dark";
    if (stored === RETIRED_CLEAR_THEME || legacy === RETIRED_CLEAR_THEME) {
      localStorage.setItem(THEME_STORAGE_KEY, choice);
      localStorage.setItem(LEGACY_THEME_STORAGE_KEY, choice);
    }
    return choice;
  } catch {
    // localStorage unavailable
  }

  return "dark";
}

/** The theme in use: the document's, else the stored choice. */
export function currentThemeChoice(): ThemeChoice {
  if (typeof document !== "undefined") {
    const active = parseThemeChoice(
      document.documentElement.dataset.themeChoice,
    );
    if (active) return active;
  }
  return getStoredThemeChoice();
}

export function applyThemeChoice(theme: ThemeChoice): void {
  const root = document.documentElement;
  root.classList.remove("dark", "light");
  root.classList.add(theme);
  root.dataset.themeChoice = theme;
}

export function hydrateStoredThemeChoice(): void {
  applyThemeChoice(getStoredThemeChoice());
}

export function setThemeChoice(theme: ThemeChoice): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    localStorage.setItem(LEGACY_THEME_STORAGE_KEY, theme);
  } catch {
    // localStorage unavailable
  }

  applyThemeChoice(theme);
  window.dispatchEvent(
    new CustomEvent(THEME_CHANGED_EVENT, { detail: { theme } }),
  );
  if ("__TAURI_INTERNALS__" in (window as unknown as Record<string, unknown>)) {
    void import("@tauri-apps/api/event")
      .then(({ emit }) => emit(THEME_TAURI_EVENT, { theme }))
      .catch(() => {});
  }
}
